import { ReactElement, MouseEvent, useEffect, useState } from "react";
import { Item } from "../../typings/items";
import brandTag from "@/icons/offer_tag.svg";
import houseIcon from "@/icons/house_icon.svg";
import { isMobile } from "react-device-detect";
import clsx from "clsx";
import config from "@/const/config";
import cleanInput from "@/helpers/encodeValue";

const ProductCard = ({ item }: { item: Item }): ReactElement => {
  const [productURL, setProductURL] = useState("");
  const [isTallImage, setIsTallImage] = useState(false);

  const handleClick = () => {
    window.open(productURL, "_blank");
  };

  const handleOfferClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    const offerURL = `${config.MARKETPLACE_URL}/search-results?brand=${cleanInput(item.offers[0].brand)}`;
    window.open(offerURL, "_self");
  };

  const handleNetworkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    const networkURL = `${item?.network?.pageUrl}`;
    window.open(networkURL, "_self");
  };

  useEffect(() => {
    const url = new URL(item.link);
    url.searchParams.delete("utm_source");
    url.searchParams.delete("utm_medium");
    url.searchParams.delete("utm_campaign");
    url.searchParams.delete("utm_content");

    url.searchParams.append(
      "intcmp",
      `${item.productId}_${item.source}_${item.title}_searchresult_body-productCard_ext`
    );

    setProductURL(String(url));

    const image = new Image();
    image.src = item.thumbnail;

    image.onload = () => {
      const ratio = image.width / image.height;
      // As the height gets bigger, the ratio gets smaller
      if (ratio < 5 / 6) {
        setIsTallImage(true);
      }
    };
  }, [item.link, item.productId, item.source, item.title, item.thumbnail]);

  return (
    <li
      role="button"
      onClick={handleClick}
      className="group relative flex flex-col w-[320px] row-span-1 col-span-1 rounded-[8px] overflow-hidden mx-auto border-[#F6F7F8] border animate-fadeIn"
      tabIndex={0}
      data-object="body-productCard"
      data-type="button"
      data-reason={`${item.productId}_${item.source}_${item.price}/${item.source}_${item.title}_click`}
    >
      <div className=" h-[222px] relative  bg-white w-full rounded-t-[8px]">
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt=""
            className={
              isTallImage
                ? "syfclickevent h-[222px] w-full object-contain rounded-t-[8px] group-hover:scale-110 transform duration-200 ease-in-out"
                : "syfclickevent h-[222px] relative w-full object-cover rounded-t-[8px] group-hover:scale-110 transform duration-200 ease-in-out"
            }
            data-object="body-productCard"
      data-type="button"
      data-reason={`${item.productId}_${item.source}_${item.price}/${item.source}_${item.title}_click`}
          />
        ) : null}
        <div className="absolute bottom-[8px] left-[16px] h-[60px] flex flex-col justify-end gap-2">
          {item.offers?.length ? (
            <a
              role="button"
              onClick={handleOfferClick}
              data-object="body-productCard-offer"
              data-type="button"
              data-reason={`${item.productId}_${item.source}_${item.price}/${item.source}_${item.title}_BrandOffers`}
              tabIndex={0}
              className={clsx(
                isMobile
                  ? "w-[155px]"
                  : "group/offer w-[25px] transition-all delay-100 duration-350 hover:w-[155px]",
                "syfclickevent h-[24px] bg-white  border-l-4 border-syfYellow flex items-center rounded-[4px] shadow-offer"
              )}
            >
              <div className="flex opacity-[86%]">
                <img src={brandTag} alt="" className="mx-[4px]" />
                <span
                  className={clsx(
                    isMobile
                      ? "opacity-100"
                      : "opacity-0 group-hover/offer:transition-all delay-100 duration-350 group-hover/offer:opacity-100",
                    "text-nowrap text-charcoal text-[12px] leading-[24px] font-semibold"
                  )}
                >
                  Brand offers available
                </span>
              </div>
            </a>
          ) : null}
          {item?.network?.description.length ? (
            <a
              role="button"
              onClick={handleNetworkClick}
              data-object="body-productCard-network"
              data-type="button"
              data-reason={`${item.productId}_${item.source}_${item.price}/${item.source}_${item.title}_SynchronyNetwork`}
              tabIndex={0}
              className={clsx(
                isMobile
                  ? "w-full"
                  : "group/network w-[28px] transition-all duration-500 hover:w-full ",
                "syfclickevent h-[24px] bg-white  border-l-4 border-syfYellow flex items-center rounded-[4px] shadow-offer"
              )}
            >
              <div className="flex opacity-[86%] items-center z-50">
                <img
                  src={item.network.icon ? item.network.icon : houseIcon}
                  alt=""
                  className="h-[15.5px] mx-[4px]"
                />
                <span
                  className={clsx(
                    isMobile
                      ? "opacity-100"
                      : "opacity-0 group-hover/network:transition-all delay-150 duration-300 group-hover/network:opacity-100",
                    "text-nowrap text-charcoal text-[12px] leading-[24px] font-semibold pr-2 bg-white rounded-r-[4px]"
                  )}
                >
                  {item?.network?.description}
                </span>
              </div>
            </a>
          ) : null}
        </div>
      </div>

      <div className="syfclickevent absolute top-[180px] right-[20px] rounded-full w-[68px] h-[68px] z-10" data-object="body-productCard"
      data-type="button"
      data-reason={`${item.productId}_${item.source}_${item.price}/${item.source}_${item.title}_click`}>
        {item.logo ? (
          <img
            src={item.logo}
            alt=""
            className="object-contain h-full w-full rounded-full shadow-brandLogo"
            height={68}
            width={68}
          />
        ) : null}
      </div>
      <div className="syfclickevent flex flex-col h-full w-full bg-syfGray px-[16px] rounded-b-[8px] justify-end" data-object="body-productCard"
      data-type="button"
      data-reason={`${item.productId}_${item.source}_${item.price}/${item.source}_${item.title}_click`}>
        <span className="text-[16px] text-slate-gray mt-[16px] leading-[24px] max-w-[220px] truncate">
          {item.source}
        </span>
        <div className="text-left my-[6px] text-[16px] leading-[24px] font-medium text-slate-gray line-clamp-1 w-full flex h-[24px] mb-[8px]">
          <div className=" w-[214px] truncate">
            <span className="group-hover:mr-1 transform duration-200 ">
              {item.title}
            </span>
          </div>
          <span className="mx-[7px]">{item.price}</span>
          <span aria-hidden="true">›</span>
        </div>
      </div>
    </li>
  );
};

export default ProductCard;
